import { Temporal } from "temporal-polyfill";
import { CredentialType } from "@api/shared-runtime/enums";
import { STATES } from "@/consts/states";
import type { Credential } from "@/features/credentials/types";
import { asDetails } from "@/features/credentials/components/credential-card/credential-details/detail-values";

export function profileText(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  return value.trim() || null;
}

export function profileYear(value: unknown): string | null {
  const text = profileText(value);
  if (!text) {
    return null;
  }
  try {
    return String(Temporal.PlainDate.from(text).year);
  } catch {
    return null;
  }
}

export function profileDateRange({
  startDate,
  endDate,
  current = false,
}: {
  startDate: unknown;
  endDate: unknown;
  current?: boolean;
}): string | null {
  const start = profileYear(startDate);
  const end = current ? "Present" : profileYear(endDate);
  return [start, end].filter(Boolean).join(" – ") || null;
}

export function licenseTitle(credential: Credential): string {
  const stateCode = credential.version?.stateCode;
  const state = STATES.find(([, code]) => code === stateCode)?.[0] ?? stateCode;
  if (credential.credentialType === CredentialType.StateLicense) {
    if (state) {
      return `${state} License`;
    }
    const details = asDetails(credential.version?.details);
    if (details.compact === true) {
      return "Interstate Medical Licensure Compact";
    }
  }
  if (credential.credentialType === CredentialType.Dea) {
    return "Federal DEA";
  }
  return [state, credential.label].filter(Boolean).join(" ");
}

export function chronologicalCredentials(
  credentials: Credential[],
): Credential[] {
  // ISO calendar dates sort chronologically. Missing dates follow known ones;
  // current positions lead even when their starting year wasn't recorded.
  return [...credentials].sort((left, right) => {
    const leftDetails = asDetails(left.version?.details);
    const rightDetails = asDetails(right.version?.details);
    const currentDifference =
      Number(rightDetails.currentlyEmployed === true) -
      Number(leftDetails.currentlyEmployed === true);
    if (currentDifference !== 0) {
      return currentDifference;
    }
    const leftDate =
      profileText(leftDetails.startDate) ?? left.version?.issuedAt ?? "";
    const rightDate =
      profileText(rightDetails.startDate) ?? right.version?.issuedAt ?? "";
    return rightDate.localeCompare(leftDate);
  });
}
